import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { familyAPI, authAPI } from '../services/api';
import TutorialModal, { TUTORIAL_STORAGE_KEY } from '../components/TutorialModal';
import './Dashboard.css';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [familyCircles, setFamilyCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [avatars, setAvatars] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCircleName, setNewCircleName] = useState('');
  const [newCircleDescription, setNewCircleDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [claimedCount, setClaimedCount] = useState(0);

  useEffect(() => {
    loadFamilyCircles();
    loadProfilePhoto();
    if (!localStorage.getItem(TUTORIAL_STORAGE_KEY)) {
      setShowTutorial(true);
    }
    const claimed = parseInt(localStorage.getItem('claimedStories') || '0', 10);
    if (claimed > 0) {
      setClaimedCount(claimed);
      localStorage.removeItem('claimedStories');
    }
  }, []);

  const loadProfilePhoto = async () => {
    try {
      const res = await authAPI.getProfilePhoto();
      setProfilePhoto(res.data.url);
    } catch (e) {
      // No photo
    }
  };

  const loadFamilyCircles = async () => {
    try {
      const res = await familyAPI.getAll();
      setFamilyCircles(res.data.familyCircles);

      const avatarMap = {};
      await Promise.all(
        res.data.familyCircles.map(async (circle) => {
          try {
            const avatarRes = await familyAPI.getAvatar(circle._id);
            avatarMap[circle._id] = avatarRes.data.url;
          } catch (err) {
            // No avatar for this circle
          }
        })
      );
      setAvatars(avatarMap);
    } catch (error) {
      console.error('Error loading family circles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCircle = async (e) => {
    e.preventDefault();
    if (!newCircleName.trim()) return;

    setCreating(true);
    try {
      const res = await familyAPI.create({
        name: newCircleName,
        description: newCircleDescription
      });
      setFamilyCircles([...familyCircles, res.data.familyCircle]);
      setShowCreateModal(false);
      setNewCircleName('');
      setNewCircleDescription('');
    } catch (error) {
      console.error('Error creating family circle:', error);
      if (error.response?.status === 403 && error.response?.data?.upgrade) {
        setShowCreateModal(false);
        setUpgradeMessage(error.response.data.message);
        setShowUpgradeModal(true);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleSelectCircle = (circle) => {
    navigate(`/family/${circle._id}`);
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-brand">
          <img className="brand-logo-full" src="/echomemoir-logo.png" alt="EchoMemoir" />
          <img className="brand-logo-icon" src="/logo.svg" alt="" aria-hidden="true" />
          <h1 className="brand-name-mobile">EchoMemoir</h1>
        </div>
        <div className="header-user">
          <div className="header-avatar" onClick={() => navigate('/settings')}>
            {profilePhoto ? (
              <img src={profilePhoto} alt={user?.displayName} className="header-avatar-img" />
            ) : (
              <span>{user?.displayName?.charAt(0)?.toUpperCase() || '?'}</span>
            )}
          </div>
          <span className="user-greeting">{t('hello')}, {user?.displayName}</span>
          <button onClick={() => setShowTutorial(true)} className="btn-header-link" title="Tutorial"><span className="hdr-icon">🎓</span><span className="hdr-text"> Tutorial</span></button>
          <button onClick={() => navigate('/help')} className="btn-header-link" title="Help"><span className="hdr-icon">❓</span><span className="hdr-text"> {t('help')}</span></button>
          <button onClick={() => navigate('/settings')} className="btn-header-link" title="Settings"><span className="hdr-icon">⚙️</span><span className="hdr-text"> {t('settings')}</span></button>
          <button onClick={logout} className="btn-logout" title="Sign out"><span className="hdr-text">{t('signOut')}</span><span className="hdr-icon-only">↪</span></button>
        </div>
      </header>

      <main className="dashboard-main">
        {claimedCount > 0 && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '14px',
            background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)',
            borderRadius: '14px', padding: '16px 20px', marginBottom: '28px',
            animation: 'fadeInAlert 0.4s ease-out'
          }}>
            <span style={{ fontSize: '28px', flexShrink: 0 }}>💌</span>
            <div style={{ flex: 1 }}>
              <p style={{ color: '#a78bfa', fontWeight: '600', fontSize: '15px', margin: '0 0 4px' }}>
                We found {claimedCount} {claimedCount === 1 ? 'memory' : 'memories'} you shared!
              </p>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', margin: 0 }}>
                Stories you submitted as a guest have been linked to your account. Open your family circle to find them in the Memories tab.
              </p>
            </div>
            <button
              onClick={() => setClaimedCount(0)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: '18px', cursor: 'pointer', flexShrink: 0, padding: '0 0 0 8px' }}
            >
              ✕
            </button>
          </div>
        )}

        <section className="welcome-section">
          <h2>{t('yourFamilyCircles')}</h2>
          <p>{t('selectCircleDesc')}</p>
        </section>

        {loading ? (
          <div className="loading-state">
            <div className="spinner-large"></div>
            <p>{t('loadingCircles')}</p>
          </div>
        ) : (
          <div className="circles-grid">
            {familyCircles.map((circle) => (
              <div 
                key={circle._id} 
                className="circle-card"
                onClick={() => handleSelectCircle(circle)}
              >
                {avatars[circle._id] ? (
                  <img src={avatars[circle._id]} alt={circle.name} className="circle-card-avatar" />
                ) : (
                  <div className="circle-icon">👨‍👩‍👧‍👦</div>
                )}
                <h3>{circle.name}</h3>
                <p>{circle.description || t('noDescription')}</p>
                <div className="circle-meta">
                  <span>{circle.members?.length || 1} {t('members')}</span>
                </div>
              </div>
            ))}

            <div 
              className="circle-card circle-card-new"
              onClick={() => setShowCreateModal(true)}
            >
              <div className="circle-icon">➕</div>
              <h3>{t('createNewCircle')}</h3>
              <p>{t('startPreserving')}</p>
            </div>
          </div>
        )}
      </main>

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('createFamilyCircle')}</h2>
            <form onSubmit={handleCreateCircle}>
              <div className="form-group">
                <label>{t('circleName')}</label>
                <input
                  type="text"
                  value={newCircleName}
                  onChange={(e) => setNewCircleName(e.target.value)}
                  placeholder={t('circleNamePlaceholder')}
                  autoFocus
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('descriptionOptional')}</label>
                <input
                  type="text"
                  value={newCircleDescription}
                  onChange={(e) => setNewCircleDescription(e.target.value)}
                  placeholder={t('descriptionPlaceholder')}
                />
              </div>
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  {t('cancel')}
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={creating}
                >
                  {creating ? t('creating') : t('createCircle')}
                </button>
           
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', maxWidth: '440px', padding: '40px 32px' }}>
            <div style={{ 
              width: '72px', height: '72px', borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139,92,246,0.2), rgba(251,191,36,0.15))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px', fontSize: '36px'
            }}>🌟</div>
            <h2 style={{ marginBottom: '8px', fontSize: '22px' }}>{t('upgradePlan') || 'Upgrade Your Plan'}</h2>
            <p style={{ 
              color: 'rgba(255,255,255,0.5)', fontSize: '14px', 
              lineHeight: '1.7', marginBottom: '20px' 
            }}>
              {upgradeMessage}
            </p>
            <div style={{
              background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)',
              borderRadius: '12px', padding: '16px', marginBottom: '24px', textAlign: 'left'
            }}>
              <p style={{ color: '#a78bfa', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                {t('upgradeUnlock')}
              </p>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', lineHeight: '1.8', margin: 0 }}>
                ✓ {t('upgradeFeature1')}<br/>
                ✓ {t('upgradeFeature2')}<br/>
                ✓ {t('upgradeFeature3')}<br/>
                ✓ {t('upgradeFeature4')}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                className="btn-secondary" 
                onClick={() => setShowUpgradeModal(false)}
                style={{ flex: 1 }}
              >
                {t('maybeLater')}
              </button>
              <button 
                className="btn-primary" 
                onClick={() => navigate('/pricing')}
                style={{ flex: 1 }}
              >
                🚀 {t('viewPlans')}
              </button>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '16px' }}>
              {t('upgradeNote')}
            </p>
          </div>
        </div>
      )}
      {showTutorial && (
        <TutorialModal onClose={() => setShowTutorial(false)} />
      )}
    </div>
  );
};

export default Dashboard;